import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  FaCheck, FaTimes, FaInfoCircle, FaSync,
  FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaSave,
  FaShieldAlt, FaExclamationTriangle, FaChevronLeft, FaChevronRight,
  FaTimes as FaClose
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ═══════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════ */
const EVENT_TYPES = [
  { value: "മാസ പൂജ",     labelMl: "മാസ പൂജ",     labelEn: "Monthly Pooja"  },
  { value: "വാർഷിക പൂജ", labelMl: "വാർഷിക പൂജ", labelEn: "Annual Pooja"   },
  { value: "വിശേഷ പൂജ",  labelMl: "വിശേഷ പൂജ",  labelEn: "Special Pooja"  },
];

const MALAYALAM_MONTHS = [
  "ജനുവരി","ഫെബ്രുവരി","മാർച്ച്","ഏപ്രിൽ","മേയ്","ജൂൺ",
  "ജൂലൈ","ഓഗസ്റ്റ്","സെപ്റ്റംബർ","ഒക്‌ടോബർ","നവംബർ","ഡിസംബർ"
];

const ENGLISH_MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const WEEKDAYS_ML = ["ഞായർ","തിങ്കൾ","ചൊവ്വ","ബുധൻ","വ്യാഴം","വെള്ളി","ശനി"];
const WEEKDAYS_EN = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

/* ═══════════════════════════════════════════════════
   TOAST
═══════════════════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const config = {
    success: { icon: <FaCheck />,       label: t("toastBlessings"),    accentColor: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)",  borderColor: "rgba(42,107,42,0.3)",  symbol: "✦" },
    error:   { icon: <FaTimes />,       label: t("toastError"),        accentColor: "#8b1a1a", iconBg: "rgba(139,26,26,0.1)",   borderColor: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaInfoCircle />,  label: t("toastDivineNotice"), accentColor: "#8a6020", iconBg: "rgba(201,151,58,0.1)",   borderColor: "rgba(201,151,58,0.3)", symbol: "ॐ" }
  };
  const c = config[type] || config.info;

  return (
    <div className="adv-toast-container">
      <div className="adv-toast-card" style={{ "--accent": c.accentColor, "--border-color": c.borderColor }}>
        <div className="adv-toast-top-bar" />
        <span className="adv-toast-corner tl" /><span className="adv-toast-corner tr" />
        <span className="adv-toast-corner bl" /><span className="adv-toast-corner br" />
        <div className="adv-toast-medallion" style={{ background: c.iconBg, borderColor: c.borderColor }}>
          <div className="adv-toast-spin-ring" />
          <div className="adv-toast-icon-inner" style={{ color: c.accentColor }}>{c.icon}</div>
        </div>
        <div className="adv-toast-content">
          <div className="adv-toast-label-row">
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
            <h4 className="adv-toast-title" style={{ color: c.accentColor }}>{c.label}</h4>
            <span className="adv-toast-symbol" style={{ color: c.accentColor }}>{c.symbol}</span>
          </div>
          <div className="adv-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accentColor}55,transparent)` }} />
          <p className="adv-toast-message">{message}</p>
        </div>
        <button className="adv-toast-close" onClick={onClose} style={{ color: c.accentColor }}>✕</button>
        <div className="adv-toast-bottom-rule" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   CONFIRM DIALOG
═══════════════════════════════════════════════════ */
function ConfirmDialog({ message, onConfirm, onCancel, confirmLabel = "Confirm", danger = false, title = "Confirm Action", icon = null, subtitle = "" }) {
  const { t } = useTranslation();
  const iconEl = icon || (danger ? <FaExclamationTriangle /> : <FaShieldAlt />);
  const accentColor  = danger ? "#8b1a1a" : "#2a6b2a";
  const accentBg     = danger ? "rgba(139,26,26,0.12)" : "rgba(42,107,42,0.1)";
  const accentBorder = danger ? "rgba(139,26,26,0.3)"  : "rgba(42,107,42,0.28)";

  return createPortal(
    <div className="adv-modal-overlay" onClick={onCancel}>
      <div className="adv-confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="adv-cm-topstrip" style={{ background: `linear-gradient(90deg, transparent, ${accentColor}88, ${accentColor}, ${accentColor}88, transparent)` }} />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />
        <div className="adv-cm-header">
          <div className="adv-cm-icon-wrap" style={{ background: accentBg, borderColor: accentBorder }}>
            <div className="adv-cm-icon-ring" style={{ borderColor: `${accentColor}44` }} />
            <div className="adv-cm-icon-ring adv-cm-icon-ring2" style={{ borderColor: `${accentColor}22` }} />
            <span className="adv-cm-icon" style={{ color: accentColor }}>{iconEl}</span>
          </div>
          <div className="adv-cm-title-area">
            <div className="adv-cm-eyebrow">✦ {t("evtConfirmEyebrow")} ✦</div>
            <h3 className="adv-cm-title">{title}</h3>
            {subtitle && <p className="adv-cm-subtitle">{subtitle}</p>}
          </div>
        </div>
        <div className="adv-cm-divider"><span className="adv-cm-divider-gem" style={{ background: accentColor }} /></div>
        <div className="adv-cm-body"><p className="adv-cm-message">{message}</p></div>
        <div className="adv-cm-actions">
          <button className="adv-btn adv-btn-ghost" onClick={onCancel}>{t("btnCancel")}</button>
          <button
            className="adv-btn adv-cm-confirm-btn"
            style={{ "--cbg": danger ? "linear-gradient(135deg,#5a0000,#8b1a1a)" : "linear-gradient(135deg,#1a4a1a,#2a6b2a)", "--cborder": accentBorder }}
            onClick={onConfirm}
          >
            {iconEl} {confirmLabel}
          </button>
        </div>
        <div className="adv-cm-bottomstrip" />
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   EVENT FORM MODAL (Add / Edit)
═══════════════════════════════════════════════════ */
function EventFormModal({ event, onClose, onSaved, showToast }) {
  const { t, i18n } = useTranslation();
  const isMl = i18n.language === "ml";
  const isEdit = !!event?.id;

  const [formData, setFormData] = useState({
    open_date:  event?.open_date  ? event.open_date.slice(0, 10) : (event?._prefillDate || ""),
    event_name: event?.event_name || EVENT_TYPES[0].value,
  });
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.open_date)   errs.open_date  = t("evtErrDate");
    if (!formData.event_name)  errs.event_name = t("evtErrName");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        open_date:  formData.open_date,
        event_name: formData.event_name,
      };

      const url    = isEdit ? `${API}/api/admin/temple-events/${event.id}` : `${API}/api/admin/temple-events`;
      const method = isEdit ? "PATCH" : "POST";

      const res  = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Operation failed");

      showToast(isEdit ? t("evtToastUpdated") : t("evtToastCreated"), "success");
      onSaved(data);
      onClose();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-edit-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />

        {/* Header */}
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <span style={{ fontSize: 26, color: "#D4AF37", filter: "drop-shadow(0 0 6px rgba(212,175,55,0.7))" }}>📅</span>
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("evtModEyebrow")} ✦</div>
            <h2 className="adv-rm-title">{isEdit ? t("evtModEdit") : t("evtModAdd")}</h2>
            <p className="adv-rm-name-tag">{isEdit ? event.event_name : t("evtModAddSub")}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaClose /></button>
        </div>

        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>

        {/* Body */}
        <div className="adv-edit-body">
          <div className="adv-edit-section-label">✦ {t("evtSecDetails")}</div>
          <div className="adv-edit-grid">

            {/* Date */}
            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("evtLblDate")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.open_date ? "err" : ""}`}>
                <span className="adv-edit-prefix"><span style={{ fontSize: 16, color: "#D4AF37", filter: "drop-shadow(0 0 3px rgba(212,175,55,0.6))" }}>📅</span></span>
                <input
                  type="date"
                  name="open_date"
                  className="adv-edit-input"
                  value={formData.open_date}
                  onChange={handleChange}
                />
              </div>
              {errors.open_date && <span className="adv-edit-err">{errors.open_date}</span>}
            </div>

            {/* Event Name Dropdown */}
            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("evtLblName")} <span className="adv-req">*</span></label>
              <div className={`adv-edit-wrap ${errors.event_name ? "err" : ""}`}>
                <span className="adv-edit-prefix"><span style={{ fontSize: 16, color: "#D4AF37", filter: "drop-shadow(0 0 3px rgba(212,175,55,0.6))" }}>🪔</span></span>
                <select
                  name="event_name"
                  className="adv-edit-input adv-edit-select"
                  value={formData.event_name}
                  onChange={handleChange}
                >
                  {EVENT_TYPES.map(et => (
                    <option key={et.value} value={et.value}>{isMl ? et.labelMl : et.labelEn}</option>
                  ))}
                </select>
              </div>
              {errors.event_name && <span className="adv-edit-err">{errors.event_name}</span>}
            </div>

          </div>

          <div className="adv-edit-actions">
            <button className="adv-btn adv-btn-ghost" onClick={onClose} disabled={loading}>{t("btnCancel")}</button>
            <button className="adv-edit-save-btn" onClick={handleSubmit} disabled={loading}>
              {loading
                ? <><span className="adv-edit-saving-spinner" />{t("btnRegistering")}...</>
                : <><FaSave /> {isEdit ? t("evtBtnUpdate") : t("evtBtnCreate")}</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   GRAND CALENDAR COMPONENT
═══════════════════════════════════════════════════ */
function EventCalendar({ events, onDayClick }) {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hoveredDay, setHoveredDay] = useState(null);
  // Calendar language: follows i18n but can be toggled independently
  const [calLang, setCalLang] = useState(i18n.language === "ml" ? "ml" : "en");

  const isMl = calLang === "ml";

  // Sync with global language changes
  useEffect(() => { setCalLang(i18n.language === "ml" ? "ml" : "en"); }, [i18n.language]);

  // All events lookup
  const eventMap = {};
  events.forEach(ev => {
    const key = ev.open_date ? ev.open_date.slice(0, 10) : null;
    if (key) eventMap[key] = ev;
  });

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,"0")}-${String(today.getDate()).padStart(2,"0")}`;

  const monthLabel   = isMl ? MALAYALAM_MONTHS[viewMonth] : ENGLISH_MONTHS[viewMonth];
  const WEEKDAYS     = isMl ? WEEKDAYS_ML : WEEKDAYS_EN;
  const WEEKDAYS_SHORT = isMl
    ? ["ഞ","തി","ചൊ","ബു","വ്യ","വെ","ശ"]
    : ["S","M","T","W","T","F","S"];

  // Count events this month
  const monthEvents = Object.entries(eventMap).filter(([k]) =>
    k.startsWith(`${viewYear}-${String(viewMonth+1).padStart(2,"0")}`)
  );

  // Event label in current language
  const getEventLabel = (ev) => {
    if (!ev?.event_name) return "";
    const match = EVENT_TYPES.find(et => et.value === ev.event_name);
    if (!match) return ev.event_name;
    return isMl ? match.labelMl : match.labelEn;
  };

  return (
    <div className="gcal-outer">
      {/* Decorative top strip */}
      <div className="gcal-topstrip">
        <div className="gcal-topstrip-line" />
        <span className="gcal-topstrip-ornament">✦ ॐ ✦</span>
        <div className="gcal-topstrip-line" />
      </div>

      <div className="gcal-wrap">
        {/* Corner ornaments */}
        <span className="gcal-corner gcal-tl" /><span className="gcal-corner gcal-tr" />
        <span className="gcal-corner gcal-bl" /><span className="gcal-corner gcal-br" />

        {/* ── HEADER ── */}
        <div className="gcal-header">
          <div className="gcal-header-bg" />
          <div className="gcal-header-rings">
            {[160,120,82].map((s,i) => <div key={i} className="gcal-h-ring" style={{width:s,height:s,animationDelay:`${i*0.6}s`}} />)}
          </div>

          <button className="gcal-nav-btn" onClick={prevMonth} aria-label="Previous">
            <FaChevronLeft />
          </button>

          <div className="gcal-header-center">
            <h2 className="gcal-month-title">{monthLabel}</h2>
            <div className="gcal-year-badge">{viewYear}</div>
          </div>

          <button className="gcal-nav-btn" onClick={nextMonth} aria-label="Next">
            <FaChevronRight />
          </button>
        </div>

        {/* ── LANG SWITCH + STATS STRIP ── */}
        <div className="gcal-stats-strip">
    

          <span className="gcal-stats-divider">❖</span>

          <span className="gcal-stat-item">
            <span className="gcal-stat-icon" style={{ color: "#D4AF37", filter: "drop-shadow(0 0 3px rgba(212,175,55,0.6))" }}>🪔</span>
            <span className="gcal-stat-val">{monthEvents.length}</span>
            <span className="gcal-stat-lbl">{isMl ? "പരിപാടികൾ" : "Events"}</span>
          </span>
          <span className="gcal-stats-divider">❖</span>
          <span className="gcal-stat-item">
            <span className="gcal-stat-icon" style={{ color: "#D4AF37", filter: "drop-shadow(0 0 3px rgba(212,175,55,0.6))" }}>☀️</span>
            <span className="gcal-stat-val">{today.getDate()}</span>
            <span className="gcal-stat-lbl">{isMl ? "ഇന്ന്" : "Today"}</span>
          </span>
        </div>

        {/* ── WEEKDAY HEADERS ── */}
        <div className="gcal-grid gcal-weekdays-row">
          {WEEKDAYS.map((d,i) => (
            <div key={i} className={`gcal-weekday-hdr${i === 0 ? " sunday" : i === 6 ? " saturday" : ""}`}>
              <span className="gcal-wday-full">{d}</span>
              <span className="gcal-wday-short">{WEEKDAYS_SHORT[i]}</span>
            </div>
          ))}
        </div>

        {/* ── DIVIDER ── */}
        <div className="gcal-hdr-divider"><span className="gcal-hdr-gem">◆</span></div>

        {/* ── DAY GRID ── */}
        <div className="gcal-grid gcal-days-grid">
          {cells.map((day, idx) => {
            if (!day) return <div key={`e-${idx}`} className="gcal-cell gcal-empty" />;

            const dateStr = `${viewYear}-${String(viewMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
            const ev      = eventMap[dateStr];
            const isToday = dateStr === todayStr;
            const isSun   = (idx % 7 === 0);
            const isSat   = (idx % 7 === 6);
            const isHov   = hoveredDay === dateStr;

            return (
              <div
                key={dateStr}
                className={[
                  "gcal-cell",
                  isToday ? "gcal-today"     : "",
                  ev      ? "gcal-has-event" : "",
                  isSun   ? "gcal-sunday"    : "",
                  isSat   ? "gcal-saturday"  : "",
                  isHov   ? "gcal-hovered"   : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onDayClick(ev || null, dateStr)}
                onMouseEnter={() => setHoveredDay(dateStr)}
                onMouseLeave={() => setHoveredDay(null)}
                title={ev ? getEventLabel(ev) : `${day} ${monthLabel}`}
              >
                {isToday && <div className="gcal-today-ring" />}
                <span className="gcal-day-num">{day}</span>
                {ev && (
                  <div className="gcal-event-block">
                    <span className="gcal-event-lamp" style={{ color: "#D4AF37", filter: "drop-shadow(0 0 4px rgba(212,175,55,0.7))" }}>🪔</span>
                    <span className="gcal-event-name">{getEventLabel(ev)}</span>
                    {ev.status === false && <span className="gcal-event-inactive-dot" title={isMl ? "നിഷ്ക്രിയം" : "Inactive"} />}
                  </div>
                )}
                {!ev && isHov && <div className="gcal-add-hint">+</div>}
              </div>
            );
          })}
        </div>

        {/* ── LEGEND ── */}
        <div className="gcal-legend">
          <span className="gcal-leg-item">
            <span className="gcal-leg-swatch gcal-leg-event" />
            <span>{isMl ? "പരിപാടി" : "Event"}</span>
          </span>
          <span className="gcal-leg-sep">·</span>
          <span className="gcal-leg-item">
            <span className="gcal-leg-swatch gcal-leg-today" />
            <span>{isMl ? "ഇന്ന്" : "Today"}</span>
          </span>
          <span className="gcal-leg-sep">·</span>
          <span className="gcal-leg-item gcal-leg-hint">
            ✦ {isMl ? "ഒരു തീയതി ക്ലിക്ക് ചെയ്യുക" : "Click any date to add or manage"}
          </span>
        </div>
      </div>

      {/* Decorative bottom strip */}
      <div className="gcal-topstrip" style={{marginTop:0}}>
        <div className="gcal-topstrip-line" />
        <span className="gcal-topstrip-ornament">✦ ॐ ✦</span>
        <div className="gcal-topstrip-line" />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   DAY DETAIL MODAL (shown when clicking a date with event)
═══════════════════════════════════════════════════ */
function DayDetailModal({ ev, onClose, onEdit, onDelete, onToggleStatus, fmtDate, t }) {
  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-edit-regal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />

        {/* Header */}
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <span style={{ fontSize: 28, color: "#D4AF37", filter: "drop-shadow(0 0 6px rgba(212,175,55,0.7))" }}>🪔</span>
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("evtModEyebrow")} ✦</div>
            <h2 className="adv-rm-title" style={{ fontSize: 16 }}>{ev.event_name}</h2>
            <p className="adv-rm-name-tag">{fmtDate(ev.open_date)}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaClose /></button>
        </div>

        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>

        {/* Body */}
        <div className="adv-edit-body" style={{ textAlign: "center" }}>
          {/* Status toggle */}
          <div style={{ marginBottom: 20 }}>
            <button
              className={`adv-status-pill ${ev.status ? "status-active" : "status-inactive"}`}
              onClick={() => { onToggleStatus(ev); onClose(); }}
            >
              {ev.status ? <FaToggleOn /> : <FaToggleOff />}
              {ev.status ? ` ${t("evtStatusActive")}` : ` ${t("evtStatusInactive")}`}
            </button>
          </div>

          <div className="adv-edit-actions">
            <button className="adv-btn adv-btn-ghost" onClick={onClose}>{t("btnCancel")}</button>
            <button
              className="adv-icon-btn adv-btn-edit-sm"
              style={{ width: "auto", padding: "8px 18px", gap: 7, fontFamily: "Cinzel, serif", fontSize: 9.5, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase" }}
              onClick={() => onEdit(ev)}
            >
              <FaEdit /> {t("evtModEdit")}
            </button>
            <button
              className="adv-icon-btn adv-btn-delete-sm"
              style={{ width: "auto", padding: "8px 18px", gap: 7, fontFamily: "Cinzel, serif", fontSize: 9.5, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase" }}
              onClick={() => onDelete(ev)}
            >
              <FaTrash /> {t("evtConfDeleteBtn")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT: ACalendar
═══════════════════════════════════════════════════ */
export default function ACalendar() {
  const { t } = useTranslation();
  const [events, setEvents]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [toast, setToast]           = useState(null);
  const [confirmDialog, setConfirm] = useState(null);
  const [formModal, setFormModal]   = useState(null);
  const [logoFace, setLogoFace]     = useState("om");
  const [selectedDayEvent, setSelectedDayEvent] = useState(null);

  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  const showToast = (message, type = "info") => setToast({ message, type });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/admin/temple-events`);
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setEvents(data);
    } catch (err) {
      showToast(t("evtErrFetch"), "error");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  /* Handlers */
  const handleAddClick  = (prefillDate) => setFormModal(prefillDate ? { _prefillDate: prefillDate } : {});
  const handleEditClick = (ev) => setFormModal(ev);

  // When a calendar day is clicked: if has event → show day detail modal; else → open add form with date pre-filled
  const handleDayClick = (ev, dateStr) => {
    if (ev) {
      setSelectedDayEvent({ ev, dateStr });
    } else {
      handleAddClick(dateStr);
    }
  };

  const handleDelete = (ev) => {
    setConfirm({
      title:        t("evtConfDeleteTitle"),
      subtitle:     t("evtConfDeleteSub"),
      message:      `"${ev.event_name}" (${ev.open_date?.slice(0,10)}) ${t("evtConfDeleteMsg")}`,
      confirmLabel: t("evtConfDeleteBtn"),
      danger:       true,
      icon:         <FaTrash />,
      onConfirm: async () => {
        try {
          const res = await fetch(`${API}/api/admin/temple-events/${ev.id}`, { method: "DELETE" });
          if (!res.ok) throw new Error("Delete failed");
          showToast(t("evtToastDeleted"), "success");
          setConfirm(null);
          fetchEvents();
        } catch (err) {
          showToast(err.message, "error");
        }
      }
    });
  };

  const handleToggleStatus = async (ev) => {
    try {
      const res = await fetch(`${API}/api/admin/temple-events/${ev.id}/status`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ status: !ev.status }),
      });
      if (!res.ok) throw new Error("Update failed");
      fetchEvents();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const handleFormSaved = () => {
    setFormModal(null);
    fetchEvents();
  };

  /* Stats */
  const stats = {
    total:    events.length,
    active:   events.filter(e => e.status).length,
    inactive: events.filter(e => !e.status).length,
  };

  /* Format date nicely — parse as local date to avoid UTC timezone shift */
  const fmtDate = (d) => {
    if (!d) return "—";
    const [year, month, day] = d.slice(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("ml-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <>
      <EventStyles />

      {/* ── HERO ── */}
      <div className="adv-hero">
        <div className="adv-hero-bg" />
        <div className="adv-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="adv-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>
        <div className="adv-hero-medallion">
          <div className="adv-hero-med-ring" />
          <div className="adv-hero-med-ring adv-hero-med-ring-2" />
          <div className={`adv-hero-face ${logoFace === "om" ? "visible" : ""}`}>
            <span className="adv-hero-om">ॐ</span>
          </div>
          <div className={`adv-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
            <img src="./logo.png" alt="Temple" className="adv-hero-logo-img" />
          </div>
          <div className="adv-hero-med-halo" />
        </div>
        <div className="adv-hero-text">
          <h1 className="adv-hero-title">{t("evtPageTitle")}</h1>
          <p className="adv-hero-sub">{t("evtPageSub")}</p>
        </div>
        <div className="adv-hero-rule" />
        <div className="adv-stat-row">
          {[
            { label: t("evtStatTotal"),    val: stats.total,    color: "#c9973a" },
            { label: t("evtStatActive"),   val: stats.active,   color: "#2a6b2a" },
            { label: t("evtStatInactive"), val: stats.inactive, color: "#8b1a1a" },
          ].map((s, i) => (
            <div key={i} className="adv-stat-card" style={{ "--scolor": s.color }}>
              <div className="adv-stat-val">{s.val}</div>
              <div className="adv-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="adv-toolbar">
        <div className="adv-toolbar-btns" style={{ marginLeft: "auto" }}>
          <button className="adv-action-btn adv-btn-refresh" onClick={fetchEvents}>
            <FaSync className={loading ? "spin" : ""} /> {t("btnRefresh")}
          </button>
        </div>
      </div>

      {/* ── CONTENT: Calendar only ── */}
      <div className="adv-table-wrap">
        {loading ? (
          <div className="adv-loader-wrap">
            <div className="adv-loader-rings">
              {[90, 70, 52].map((s, i) => (
                <div key={i} className="adv-loader-ring" style={{ width: s, height: s, animationDelay: `${i * 0.3}s` }} />
              ))}
              <div className="adv-loader-om">ॐ</div>
            </div>
            <p className="adv-loader-text">{t("evtLoading")}</p>
          </div>
        ) : (
          <EventCalendar
            events={events}
            onDayClick={handleDayClick}
          />
        )}
      </div>

      {/* ── MODALS ── */}
      {selectedDayEvent && (
        <DayDetailModal
          ev={selectedDayEvent.ev}
          onClose={() => setSelectedDayEvent(null)}
          onEdit={(ev) => { setSelectedDayEvent(null); handleEditClick(ev); }}
          onDelete={(ev) => { setSelectedDayEvent(null); handleDelete(ev); }}
          onToggleStatus={handleToggleStatus}
          fmtDate={fmtDate}
          t={t}
        />
      )}
      {formModal !== null && (
        <EventFormModal
          event={formModal.id ? formModal : (formModal._prefillDate ? { _prefillDate: formModal._prefillDate } : null)}
          onClose={() => setFormModal(null)}
          onSaved={handleFormSaved}
          showToast={showToast}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          title={confirmDialog.title}
          subtitle={confirmDialog.subtitle}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          icon={confirmDialog.icon}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function EventStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }

      /* ── HERO ── */
      .adv-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; border-radius: 16px 16px 0 0; }
      .adv-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .adv-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .adv-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: heroRingPulse 4s ease-in-out infinite alternate; }
      .adv-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes heroRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .adv-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,248,231,0.06); }
      .adv-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: slowSpin 10s linear infinite; }
      .adv-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .adv-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .adv-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .adv-hero-om { font-family: 'Cinzel Decorative', cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .adv-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .adv-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .adv-hero-text { text-align: center; position: relative; z-index: 1; }
      .adv-hero-eyebrow { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.65); text-transform: uppercase; margin-bottom: 10px; }
      .adv-hero-title { font-family: 'Cinzel Decorative', cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .adv-hero-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.7); margin-bottom: 20px; }
      .adv-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto 24px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .adv-stat-row { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; padding: 0 0 28px; position: relative; z-index: 1; }
      .adv-stat-card { background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.18); padding: 14px 22px; text-align: center; clip-path: polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%); transition: background 0.2s; min-width: 90px; }
      .adv-stat-card:hover { background: rgba(201,151,58,0.08); border-color: rgba(201,151,58,0.35); }
      .adv-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; color: var(--scolor, var(--gold-bright)); }
      .adv-stat-label { font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(245,228,176,0.75); margin-top: 3px; }

      /* ── TOOLBAR ── */
      .adv-toolbar { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; padding: 20px 28px 12px; background: var(--parchment); border-bottom: 1px solid rgba(201,151,58,0.15); position: sticky; top: 0; z-index: 10; box-shadow: 0 4px 12px rgba(26,4,4,0.06); }
      .adv-search-wrap { flex: 1; min-width: 220px; display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
      .adv-search-wrap:focus-within { border-color: var(--gold-mid); box-shadow: 0 0 0 3px rgba(201,151,58,0.1); }
      .adv-search-icon { margin: 0 10px 0 18px; color: var(--gold-dim); font-size: 13px; flex-shrink: 0; }
      .adv-search-input { flex: 1; border: none; outline: none; background: transparent; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 11px 8px; }
      .adv-search-input::placeholder { color: rgba(74,26,8,0.28); font-style: italic; }
      .adv-search-clear { background: none; border: none; cursor: pointer; padding: 0 14px; color: rgba(74,26,8,0.35); font-size: 11px; transition: color 0.2s; }
      .adv-search-clear:hover { color: #8b1a1a; }
      .adv-toolbar-btns { display: flex; gap: 8px; margin-left: auto; align-items: center; flex-wrap: wrap; }
      .adv-action-btn { display: flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); transition: all 0.25s; }
      .adv-btn-refresh { background: rgba(201,151,58,0.1); border: 1px solid rgba(201,151,58,0.3); color: var(--gold-dim); }
      .adv-btn-refresh:hover { background: rgba(201,151,58,0.18); }
      .adv-btn-export { background: linear-gradient(135deg, #1a4d1a, #2a6b2a); color: #c8f0c8; border: 1px solid rgba(42,107,42,0.4); }
      .adv-btn-export:hover { filter: brightness(1.1); }
      .spin { animation: spinIcon 0.8s linear infinite; }
      @keyframes spinIcon { to { transform: rotate(360deg); } }

      /* ── VIEW TOGGLE ── */
      .evt-view-toggle { display: flex; background: rgba(201,151,58,0.08); border: 1px solid rgba(201,151,58,0.25); border-radius: 4px; overflow: hidden; }
      .evt-toggle-btn { display: flex; align-items: center; gap: 5px; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 8px 14px; border: none; background: none; cursor: pointer; color: var(--gold-dim); transition: all 0.2s; }
      .evt-toggle-btn.active { background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); color: var(--gold-bright); }
      .evt-toggle-btn:hover:not(.active) { background: rgba(201,151,58,0.12); }

      /* ── TABLE ── */
      .adv-table-wrap { padding: 0; background: var(--parchment); overflow-x: auto; }
      .adv-table { width: 100%; border-collapse: separate; border-spacing: 0 6px; font-family: 'EB Garamond', serif; }
      .adv-table thead tr { background: linear-gradient(135deg, var(--hero-1) 0%, var(--hero-2) 55%, var(--hero-3) 100%); }
      .adv-table thead tr th { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-bright); padding: 14px 14px; text-align: left; border-bottom: 1px solid rgba(201,151,58,0.25); white-space: nowrap; position: relative; }
      .adv-table thead tr th:first-child { border-radius: 3px 0 0 3px; padding-left: 18px; }
      .adv-table thead tr th:last-child  { border-radius: 0 3px 3px 0; }
      .adv-table-row { background: linear-gradient(135deg, #FDF3E3, #faf0dc); border: 1px solid rgba(201,151,58,0.15); animation: rowIn 0.35s ease both; transition: box-shadow 0.2s, transform 0.2s; }
      .adv-table-row:hover { box-shadow: 0 4px 18px rgba(26,4,4,0.1); transform: translateY(-1px); }
      @keyframes rowIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      .adv-table-row td { padding: 12px 14px; vertical-align: middle; font-size: 14px; color: var(--text-mid); border-top: 1px solid rgba(201,151,58,0.1); border-bottom: 1px solid rgba(201,151,58,0.1); }
      .adv-table-row td:first-child { border-left: 1px solid rgba(201,151,58,0.1); border-radius: 2px 0 0 2px; }
      .adv-table-row td:last-child  { border-right: 1px solid rgba(201,151,58,0.1); border-radius: 0 2px 2px 0; }
      .adv-td-num  { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.35); width: 36px; }
      .adv-td-date { font-family: 'Cinzel', serif; font-size: 10px; color: rgba(74,26,8,0.5); white-space: nowrap; }
      .adv-action-cell { display: flex; gap: 5px; align-items: center; flex-wrap: wrap; }
      .adv-icon-btn { width: 30px; height: 30px; border: 1px solid; border-radius: 2px; display: flex; align-items: center; justify-content: center; font-size: 12px; cursor: pointer; background: none; transition: all 0.18s; flex-shrink: 0; }
      .adv-btn-edit-sm       { border-color: rgba(100,74,140,0.35); color: #6a4a8c; }
      .adv-btn-edit-sm:hover { background: rgba(100,74,140,0.1); }
      .adv-btn-delete-sm       { border-color: rgba(139,26,26,0.25); color: rgba(139,26,26,0.7); }
      .adv-btn-delete-sm:hover { background: rgba(139,26,26,0.08); color: #8b1a1a; }

      /* Event Name Badge */
      .evt-event-name-badge { display: inline-flex; align-items: center; gap: 6px; font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-dark); background: rgba(201,151,58,0.07); border: 1px solid rgba(201,151,58,0.2); padding: 4px 12px; border-radius: 2px; }
      .evt-lamp { line-height: 1; }

      /* Status Pill */
      .adv-status-pill { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; padding: 5px 12px; border: 1px solid; display: inline-flex; align-items: center; gap: 6px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 50%,calc(100% - 8px) 100%,8px 100%,0 50%); transition: all 0.2s; cursor: pointer; }
      .status-active   { color: #2a6b2a; background: rgba(42,107,42,0.1);   border-color: rgba(42,107,42,0.3);   }
      .status-active:hover { background: rgba(42,107,42,0.2); }
      .status-inactive { color: #8b1a1a; background: rgba(139,26,26,0.1);  border-color: rgba(139,26,26,0.3);  }
      .status-inactive:hover { background: rgba(139,26,26,0.2); }

      /* ── LOADER & EMPTY ── */
      .adv-loader-wrap { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; gap: 14px; }
      .adv-loader-rings { position: relative; width: 100px; height: 100px; margin-bottom: 10px; }
      .adv-loader-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: loaderPulse 2s ease-in-out infinite; }
      .adv-loader-ring:nth-child(2) { border-style: solid; border-color: rgba(201,151,58,0.15); }
      .adv-loader-ring:nth-child(3) { animation: slowSpin 3s linear infinite; border-color: rgba(201,151,58,0.25); }
      @keyframes loaderPulse { 0%,100% { opacity: 0.3; transform: translate(-50%,-50%) scale(0.95); } 50% { opacity: 1; transform: translate(-50%,-50%) scale(1.02); } }
      .adv-loader-om { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); font-family: 'Cinzel Decorative', cursive; font-size: 28px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-loader-text { font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px; color: rgba(74,26,8,0.7); }
      .adv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 0; gap: 14px; text-align: center; }
      .adv-empty-om { font-family: 'Cinzel Decorative', cursive; font-size: 52px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; opacity: 0.35; }
      .adv-empty-title { font-family: 'Cinzel Decorative', cursive; font-size: 20px; background: linear-gradient(135deg, var(--crimson-rich), var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-empty-sub { font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px; color: rgba(74,26,8,0.65); max-width: 320px; }

      /* ── BUTTONS ── */
      .adv-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .adv-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .adv-btn-ghost { background: none; border-color: rgba(201,151,58,0.4); color: rgba(74,26,8,0.75); }
      .adv-btn-ghost:hover { border-color: rgba(201,151,58,0.65); background: rgba(201,151,58,0.08); color: rgba(74,26,8,0.9); }

      /* ── MODAL ── */
      .adv-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(8,1,1,0.85); backdrop-filter: blur(7px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: overlayIn 0.2s ease; }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
      .adv-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.7); animation: regalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes regalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .adv-edit-regal { max-width: 480px; max-height: 92vh; overflow-y: auto; }
      .adv-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent 0%, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent 100%); z-index: 10; }
      .adv-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .adv-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .adv-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .adv-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .adv-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }
      .adv-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .adv-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .adv-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .adv-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .adv-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .adv-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: slowSpin 9s linear infinite; }
      .adv-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .adv-rm-header-eyebrow { font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.6); text-transform: uppercase; }
      .adv-rm-title { font-family: 'Cinzel Decorative', cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-rm-name-tag { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.65); }
      .adv-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .adv-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }
      .adv-edit-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .adv-edit-section-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: #7a4e18; margin-bottom: 18px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }
      .adv-edit-grid .full-width { grid-column: span 2; }
      .adv-edit-field { display: flex; flex-direction: column; gap: 5px; }
      .adv-edit-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #6b3a12; }
      .adv-req { color: #b52020; font-weight: 900; margin-left: 2px; }
      .adv-edit-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.3s, box-shadow 0.3s; }
      .adv-edit-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .adv-edit-wrap.err { border-color: #b52020; background: rgba(181,32,32,0.02); }
      .adv-edit-prefix { font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.2); flex-shrink: 0; }
      .adv-edit-input { flex: 1; border: none; outline: none; font-family: 'EB Garamond', serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; background: transparent; }
      .adv-edit-select { appearance: none; cursor: pointer; }
      .adv-edit-err { font-size: 10px; color: #b52020; font-family: 'EB Garamond', serif; font-style: italic; margin-top: 2px; }
      .adv-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: center; margin-top: 4px; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); }
      .adv-edit-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3); transition: all 0.35s ease; }
      .adv-edit-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .adv-edit-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; }
      .adv-edit-saving-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: var(--crimson-deep); animation: spinIcon 0.7s linear infinite; }

      /* ── CONFIRM MODAL ── */
      .adv-confirm-modal { position: relative; width: 100%; max-width: 420px; background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc); border: 1px solid rgba(201,151,58,0.35); clip-path: polygon(0 18px,18px 0,calc(100% - 18px) 0,100% 18px,100% calc(100% - 18px),calc(100% - 18px) 100%,18px 100%,0 calc(100% - 18px)); box-shadow: 0 32px 90px rgba(26,4,4,0.68); animation: regalIn 0.3s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      .adv-cm-topstrip { height: 3px; width: 100%; }
      .adv-cm-header { padding: 30px 28px 0; display: flex; flex-direction: column; align-items: center; text-align: center; gap: 10px; position: relative; z-index: 1; }
      .adv-cm-icon-wrap { position: relative; width: 68px; height: 68px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 1px solid; }
      .adv-cm-icon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed; animation: slowSpin 8s linear infinite; }
      .adv-cm-icon-ring2 { inset: -12px; animation-duration: 15s; animation-direction: reverse; }
      .adv-cm-icon { font-size: 24px; position: relative; z-index: 1; }
      .adv-cm-title-area { display: flex; flex-direction: column; align-items: center; gap: 4px; }
      .adv-cm-eyebrow { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 3px; color: rgba(74,26,8,0.55); text-transform: uppercase; }
      .adv-cm-title { font-family: 'Cinzel Decorative', cursive; font-size: 18px; font-weight: 900; background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .adv-cm-subtitle { font-family: 'EB Garamond', serif; font-style: italic; font-size: 13px; color: rgba(74,26,8,0.65); }
      .adv-cm-divider { display: flex; align-items: center; justify-content: center; height: 1px; margin: 18px 36px 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); position: relative; }
      .adv-cm-divider-gem { width: 6px; height: 6px; transform: rotate(45deg); position: absolute; }
      .adv-cm-body { padding: 16px 32px 20px; }
      .adv-cm-message { font-family: 'EB Garamond', serif; font-size: 16px; color: var(--text-mid); line-height: 1.7; text-align: center; }
      .adv-cm-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; padding: 0 28px 24px; }
      .adv-cm-confirm-btn { background: var(--cbg, linear-gradient(135deg,#1a4a1a,#2a6b2a)) !important; color: #e8ffe8 !important; border-color: var(--cborder, rgba(42,107,42,0.4)) !important; }
      .adv-cm-confirm-btn:hover { filter: brightness(1.12); }
      .adv-cm-bottomstrip { height: 1px; margin: 0 24px 14px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.25), transparent); }

      /* ── GRAND CALENDAR ── */
      .gcal-outer { padding: 28px 24px 20px; background: var(--parchment); }
      .gcal-topstrip { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
      .gcal-topstrip-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }
      .gcal-topstrip-ornament { font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 4px; color: rgba(201,151,58,0.8); white-space: nowrap; font-weight: 600; }
      .gcal-wrap { position: relative; background: linear-gradient(160deg,#FDF3E3 0%,#faf0dc 55%,#f5e8cc 100%); border: 1px solid rgba(201,151,58,0.3); border-radius: 12px; box-shadow: 0 8px 40px rgba(26,4,4,0.1), 0 2px 8px rgba(26,4,4,0.05); overflow: hidden; }
      .gcal-corner { position: absolute; width: 20px; height: 20px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 5; pointer-events: none; }
      .gcal-tl { top: 12px; left: 12px; border-width: 2px 0 0 2px; }
      .gcal-tr { top: 12px; right: 12px; border-width: 2px 2px 0 0; }
      .gcal-bl { bottom: 12px; left: 12px; border-width: 0 0 2px 2px; }
      .gcal-br { bottom: 12px; right: 12px; border-width: 0 2px 2px 0; }
      .gcal-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 60%, #0a0101 100%); padding: 36px 28px 32px; display: flex; align-items: center; justify-content: space-between; }
      .gcal-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 60%, rgba(160,35,35,0.3) 0%, transparent 65%), radial-gradient(ellipse at 15% 20%, rgba(201,151,58,0.07) 0%, transparent 45%); pointer-events: none; }
      .gcal-header-rings { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); pointer-events: none; }
      .gcal-h-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: heroRingPulse 4s ease-in-out infinite alternate; }
      .gcal-h-ring:nth-child(odd) { border-style: dashed; }
      .gcal-header-center { position: relative; z-index: 2; text-align: center; flex: 1; }
      .gcal-header-eyebrow { font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 3px; color: rgba(232,196,106,0.65); text-transform: uppercase; margin-bottom: 8px; }
      .gcal-month-title { font-family: 'Cinzel Decorative', cursive; font-size: 32px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.1; margin: 0 0 10px; filter: drop-shadow(0 2px 8px rgba(232,196,106,0.2)); }
      .gcal-year-badge { display: inline-block; font-family: 'Cinzel', serif; font-size: 12px; font-weight: 700; letter-spacing: 4px; color: rgba(232,196,106,0.85); border: 1px solid rgba(232,196,106,0.4); padding: 3px 16px; border-radius: 2px; background: rgba(255,255,255,0.06); }
      .gcal-nav-btn { position: relative; z-index: 2; background: rgba(255,255,255,0.08); border: 1px solid rgba(232,196,106,0.4); color: rgba(232,196,106,0.9); width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.25s; font-size: 13px; flex-shrink: 0; }
      .gcal-nav-btn:hover { background: rgba(232,196,106,0.18); border-color: rgba(232,196,106,0.7); color: var(--gold-bright); transform: scale(1.08); box-shadow: 0 0 16px rgba(232,196,106,0.2); }
      .gcal-stats-strip { display: flex; align-items: center; justify-content: center; gap: 20px; padding: 12px 28px; background: linear-gradient(90deg, rgba(26,4,4,0.04), rgba(201,151,58,0.08), rgba(26,4,4,0.04)); border-bottom: 1px solid rgba(201,151,58,0.2); flex-wrap: wrap; }
      .gcal-stat-item { display: flex; align-items: center; gap: 7px; }
      .gcal-stat-icon { font-size: 14px; line-height: 1; }
      .gcal-stat-val { font-family: 'Cinzel Decorative', cursive; font-size: 18px; font-weight: 900; color: var(--gold-mid); }
      .gcal-stat-lbl { font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #6b3a1a; font-weight: 600; }
      .gcal-stats-divider { font-family: 'Cinzel', serif; font-size: 12px; color: rgba(201,151,58,0.5); font-weight: 700; }
      /* Language toggle */
      .gcal-lang-toggle { display: flex; border: 1px solid rgba(201,151,58,0.4); border-radius: 4px; overflow: hidden; }
      .gcal-lang-btn { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 1px; padding: 5px 12px; border: none; background: none; cursor: pointer; color: #6b3a1a; transition: all 0.2s; line-height: 1.4; }
      .gcal-lang-btn.active { background: linear-gradient(135deg, var(--hero-1), var(--hero-2)); color: var(--gold-bright); }
      .gcal-lang-btn:not(.active):hover { background: rgba(201,151,58,0.12); }
      .gcal-grid { display: grid; grid-template-columns: repeat(7, 1fr); }
      .gcal-weekdays-row { background: linear-gradient(135deg, #1a0505 0%, #0e0202 100%); border-bottom: 2px solid rgba(201,151,58,0.3); }
      .gcal-weekday-hdr { padding: 12px 4px; text-align: center; display: flex; flex-direction: column; align-items: center; gap: 2px; }
      .gcal-wday-full { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 1px; color: #e8c46a; text-transform: uppercase; }
      .gcal-wday-short { display: none; font-family: 'Cinzel', serif; font-size: 9px; font-weight: 700; color: #e8c46a; }
      .gcal-weekday-hdr.sunday .gcal-wday-full, .gcal-weekday-hdr.sunday .gcal-wday-short { color: #f5a070; }
      .gcal-weekday-hdr.saturday .gcal-wday-full, .gcal-weekday-hdr.saturday .gcal-wday-short { color: #f0d070; }
      .gcal-hdr-divider { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent); position: relative; }
      .gcal-hdr-gem { position: absolute; font-size: 9px; color: var(--gold-mid); background: var(--parchment); padding: 0 6px; line-height: 1; }
      .gcal-days-grid { padding: 10px; gap: 5px; }
      .gcal-cell { position: relative; min-height: 80px; border-radius: 6px; border: 1px solid transparent; display: flex; flex-direction: column; align-items: center; padding: 8px 5px 6px; cursor: pointer; transition: all 0.2s cubic-bezier(0.23,1,0.32,1); background: transparent; overflow: hidden; }
      .gcal-empty { visibility: hidden; cursor: default; pointer-events: none; }
      .gcal-cell:not(.gcal-empty):hover { background: rgba(201,151,58,0.1); border-color: rgba(201,151,58,0.35); transform: translateY(-2px); box-shadow: 0 4px 14px rgba(26,4,4,0.1); }
      .gcal-cell.gcal-sunday .gcal-day-num { color: #c05030; }
      .gcal-cell.gcal-saturday .gcal-day-num { color: #9a6e20; }
      .gcal-cell.gcal-today { background: rgba(201,151,58,0.14); border-color: rgba(201,151,58,0.55); }
      .gcal-cell.gcal-today:hover { background: rgba(201,151,58,0.2); }
      .gcal-today-ring { position: absolute; inset: -1px; border-radius: 6px; border: 2px solid rgba(201,151,58,0.6); animation: todayPulse 2.5s ease-in-out infinite; pointer-events: none; }
      @keyframes todayPulse { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
      .gcal-cell.gcal-today .gcal-day-num { color: #8a5c10; font-weight: 900; font-size: 15px; }
      .gcal-cell.gcal-has-event { background: linear-gradient(160deg, rgba(44,8,8,0.1) 0%, rgba(139,26,26,0.07) 100%); border-color: rgba(139,26,26,0.35); }
      .gcal-cell.gcal-has-event:hover { background: linear-gradient(160deg, rgba(44,8,8,0.16) 0%, rgba(139,26,26,0.12) 100%); border-color: rgba(139,26,26,0.6); transform: translateY(-3px); box-shadow: 0 6px 20px rgba(26,4,4,0.14); }
      .gcal-day-num { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 700; color: #3a1a08; line-height: 1; margin-bottom: 4px; z-index: 1; }
      .gcal-has-event .gcal-day-num { color: #2a0808; font-weight: 800; }
      .gcal-event-block { display: flex; flex-direction: column; align-items: center; gap: 3px; width: 100%; z-index: 1; }
      .gcal-event-lamp { font-size: 14px; line-height: 1; filter: drop-shadow(0 1px 4px rgba(212,175,55,0.75)); }
      .gcal-event-name { font-family: 'EB Garamond', serif; font-size: 10px; color: #3d0c0c; text-align: center; line-height: 1.3; max-width: 100%; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; font-style: italic; font-weight: 600; background: rgba(201,151,58,0.18); padding: 2px 5px; border-radius: 2px; border: 1px solid rgba(201,151,58,0.3); width: 100%; }
      .gcal-event-inactive-dot { width: 5px; height: 5px; border-radius: 50%; background: #8b1a1a; border: 1px solid rgba(139,26,26,0.6); }
      .gcal-add-hint { position: absolute; bottom: 6px; right: 7px; font-family: 'Cinzel', serif; font-size: 16px; font-weight: 700; color: rgba(201,151,58,0.55); line-height: 1; pointer-events: none; animation: fadeIn 0.15s ease; }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      .gcal-legend { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 12px; padding: 14px 24px 16px; border-top: 1px solid rgba(201,151,58,0.2); }
      .gcal-leg-item { display: flex; align-items: center; gap: 6px; font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 1px; text-transform: uppercase; color: #5a2e10; font-weight: 600; }
      .gcal-leg-sep { color: rgba(201,151,58,0.5); font-size: 14px; }
      .gcal-leg-swatch { width: 12px; height: 12px; border-radius: 3px; flex-shrink: 0; }
      .gcal-leg-event { background: rgba(44,8,8,0.12); border: 1px solid rgba(139,26,26,0.45); }
      .gcal-leg-today { background: rgba(201,151,58,0.2); border: 1px solid rgba(201,151,58,0.6); }
      .gcal-leg-hint { font-style: italic; color: #5a2e10; font-family: 'EB Garamond', serif; font-size: 12px; font-weight: 500; letter-spacing: 0; text-transform: none; }

      /* ── TOAST ── */
      .adv-toast-container { position: fixed; top: 28px; right: 28px; z-index: 2000; animation: toastIn 0.4s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes toastIn { from { opacity: 0; transform: translateX(60px) scale(0.95); } to { opacity: 1; transform: translateX(0) scale(1); } }
      .adv-toast-card { position: relative; width: 340px; background: linear-gradient(145deg,#FDF3E3,#faf0dc,#f5e8cc); border: 1px solid var(--border-color); clip-path: polygon(0 12px,12px 0,calc(100% - 12px) 0,100% 12px,100% calc(100% - 12px),calc(100% - 12px) 100%,12px 100%,0 calc(100% - 12px)); box-shadow: 0 12px 32px rgba(26,4,4,0.14); padding: 0 0 14px; overflow: hidden; }
      .adv-toast-top-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .adv-toast-corner { position: absolute; width: 10px; height: 10px; border-color: rgba(201,151,58,0.45); border-style: solid; }
      .adv-toast-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .adv-toast-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .adv-toast-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .adv-toast-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .adv-toast-medallion { position: absolute; left: 18px; top: 26px; width: 36px; height: 36px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .adv-toast-spin-ring { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.3); animation: slowSpin 8s linear infinite; }
      .adv-toast-icon-inner { font-size: 14px; position: relative; z-index: 1; }
      .adv-toast-content { padding: 0 40px 0 66px; }
      .adv-toast-label-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .adv-toast-symbol { font-size: 9px; opacity: 0.7; }
      .adv-toast-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .adv-toast-rule { height: 1px; margin-bottom: 7px; }
      .adv-toast-message { font-family: 'EB Garamond', serif; font-style: italic; font-size: 15px; color: rgba(74,26,8,0.85); line-height: 1.5; }
      .adv-toast-bottom-rule { height: 1px; margin: 12px 20px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.25),transparent); }
      .adv-toast-close { position: absolute; top: 22px; right: 14px; background: none; border: none; font-size: 13px; cursor: pointer; opacity: 0.45; transition: opacity 0.2s; }
      .adv-toast-close:hover { opacity: 0.9; }

      /* ── RESPONSIVE ── */
      .adv-desktop-only { display: table; width: 100%; }
      .adv-mobile-only  { display: none !important; }
      @media (max-width: 768px) {
        .adv-desktop-only { display: none !important; }
        .adv-mobile-only  { display: flex !important; }
        .adv-hero { padding: 22px 16px 0; border-radius: 0; }
        .adv-hero-title { font-size: 19px; }
        .adv-stat-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; width: 100%; padding: 0 0 18px; }
        .adv-stat-card { min-width: unset; padding: 12px 10px; clip-path: none; border-radius: 8px; }
        .adv-toolbar { padding: 12px 14px 10px; gap: 9px; flex-direction: column; align-items: stretch; }
        .adv-search-wrap { min-width: unset; width: 100%; clip-path: none; border-radius: 28px; }
        .adv-toolbar-btns { width: 100%; flex-wrap: wrap; gap: 8px; margin-left: 0; justify-content: space-between; }
        .adv-action-btn { clip-path: none; border-radius: 28px; justify-content: center; padding: 11px 10px; font-size: 9px; }
        .adv-table-wrap { padding: 0 0 40px; }
        .adv-modal-overlay { padding: 0; align-items: flex-end; }
        .adv-regal-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-height: 93vh; width: 100%; max-width: 100% !important; }
        .adv-edit-body { padding: 16px 16px 28px; }
        .adv-edit-grid { grid-template-columns: 1fr; }
        .adv-edit-grid .full-width { grid-column: span 1; }
        .adv-edit-save-btn { clip-path: none; border-radius: 28px; flex: 1; justify-content: center; }
        .adv-confirm-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-width: 100%; width: 100%; }
        .adv-btn { clip-path: none; border-radius: 28px; }
        .adv-toast-container { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .adv-toast-card { width: 100%; clip-path: none; border-radius: 12px; }
        /* Grand calendar mobile */
        .gcal-outer { padding: 16px 10px 12px; }
        .gcal-month-title { font-size: 22px; }
        .gcal-nav-btn { width: 34px; height: 34px; font-size: 11px; }
        .gcal-cell { min-height: 52px; padding: 5px 2px 4px; }
        .gcal-day-num { font-size: 12px; }
        .gcal-wday-full { display: none; }
        .gcal-wday-short { display: block; }
        .gcal-weekday-hdr { padding: 8px 3px; }
        .gcal-event-name { display: none; }
        .gcal-event-lamp { font-size: 12px; }
        .gcal-stats-strip { gap: 10px; padding: 10px 12px; }
        .gcal-stat-val { font-size: 14px; }
        .gcal-stat-lbl { font-size: 8px; }
        .gcal-days-grid { padding: 6px; gap: 3px; }
        .gcal-lang-btn { font-size: 9px; padding: 4px 10px; }
      }
    `}</style>
  );
}